<script lang="ts" setup>
import { DefaultButton } from '@hantecmarket/hantec-ui-widget'
import { onMounted, ref } from 'vue'

import AwardIcon from '@/components/Icons/AwardIcon.vue'
import CardWrapper from '@/components/wrappers/CardWrapper.vue'
import ListWrapper from '@/components/wrappers/ListWrapper.vue'
import SectionWrapper from '@/components/wrappers/SectionWrapper.vue'
import { getLanguage } from '@/helpers/language-helper'
import {
  fetchHeroStore,
  useHeroAdvantagesItems,
  useHeroDemoButton,
  useHeroImageDesktop,
  useHeroImageMobile,
  useHeroOpenAccountButton,
  useHeroTitle,
  useSponsorLogo,
  useSponsorTitle
} from '@/services/HomePage/heroService'
import { isSponsorshipAtleticoPresent } from '@/services/navigationService'
import { resolveButterUrl } from '@/utils/utils'

fetchHeroStore()

const language = ref('')
language.value = getLanguage() || ''

const title = useHeroTitle()
const openAccountButton = useHeroOpenAccountButton()
const demoButton = useHeroDemoButton()
const imageDesktop = useHeroImageDesktop()
const imageMobile = useHeroImageMobile()
const advantages = useHeroAdvantagesItems()
const pattern = ref<any>()
const logo = useSponsorLogo()
const isPublished = isSponsorshipAtleticoPresent()
const sponsorTitle = useSponsorTitle()

function loadImage() {
  return new Promise((resolve, reject) => {
    setTimeout(resolve, 0)
  })
}

onMounted(() => {
  const img = new Image()
  if (imageMobile.value) {
    img.src = imageMobile.value
    img.onload = async () => {
      await loadImage()
      pattern.value = img.src
    }
  }
})
</script>

<template>
  <div class="relative w-full h-full bg-black pb-2.5 md:pb-0 bg-no-repeat bg-center-26">
    <img
      :src="pattern"
      alt="background image iphone"
      class="absolute z-10 md:hidden w-full"
      fetchpriority="high"
      height="900"
    />
    <div class="absolute inset-0"></div>
    <SectionWrapper
      class="relative z-10 px-0 pt-26.25 pb-0 md:bg-custom-radial-lower md:pt-30 md:px-15 lg:px-30"
    >
      <template v-slot:header>
        <p class="text-white h2-bold md:h1-hero-bold"><span v-html="title"></span></p>
      </template>
      <template v-slot:content>
        <div
          class="flex pt-23 md:pt-12.5 justify-center gap-2 flex-col md:flex-row px-2.5 md:pb-15"
        >
          <DefaultButton
            :href="resolveButterUrl(openAccountButton.url)"
            class="text-gray-50 px-1.5 md:px-8 py-4 sm:flex items-center uppercase text-base rounded-1.5 md:leading-normal md:w-54.5 font-bold whitespace-nowrap h-12.5 leading-normal mb-5 md:mb-0 justify-center"
          >
            <div>{{ openAccountButton.title }}</div>
          </DefaultButton>
          <DefaultButton
            :href="resolveButterUrl(demoButton.url)"
            class="bg-transparent md:bg-hantec-secondary text-hantec-primary md:text-white px-1.5 md:px-8 md:py-4 sm:flex items-center uppercase text-base md:border-2 md:border-white md:rounded-1.5 md:leading-normal justify-center md:w-54.5 font-bold h-12.5 leading-normal"
          >
            <div>{{ demoButton.title }}</div>
          </DefaultButton>
        </div>
        <div v-if="isPublished" class="flex justify-center md:mb-[60px] mt-[78px] md:mt-0">
          <div class="flex flex-col text-center">
            <img :src="logo" />
            <div class="nav-s uppercase text-white pt-2.5">{{ sponsorTitle }}</div>
          </div>
        </div>
        <ListWrapper
          class="flex flex-wrap gap-5 px-12 mx-auto bg-transparent max-w-627 md:grid md:grid-cols-5 md:pb-0 md:pt-0 md:px-0 md:bg-black"
        >
          <template v-slot="{ item }">
            <CardWrapper
              :cardDetails="item"
              class="w-1/4 md:w-auto md:col-span-1 border-none p-0 flex flex-col items-center brightness-200 md:brightness-100 md:ml-0 md:pb-13.5"
              name="item"
            >
              <template v-slot:card-image>
                <AwardIcon class="shrink-0 aspect-square w-8 h-7 md:h-10 md:w-11.25"></AwardIcon>
              </template>
              <template v-slot:card-header>
                <p
                  class="text-white w-[95px] text-xxxs md:text-xxs font-bold pt-2 md:pt-2.5 md:pb-0.5 leading-normal"
                >
                  {{ item.title }}
                </p>
              </template>
              <template v-slot:card-description>
                <p class="font-bold leading-normal text-gray-350 text-xxxs md:text-xxs">
                  {{ item.globality }}
                </p>
              </template>
            </CardWrapper>
          </template>
        </ListWrapper>

        <div class="hidden overflow-hidden max-h-376 md:block">
          <img :src="imageDesktop" alt="" class="mx-auto max-w-610" />
        </div>
        <ListWrapper
          :itemsList="advantages"
          class="max-w-1440 mx-auto py-5 px-0 justify-evenly bg-gray-850 rounded-2.5 hidden md:flex md:px-2.5 lg:px-0"
        >
          <template v-slot="{ item }">
            <CardWrapper
              :cardDetails="item"
              class="flex flex-col items-center p-0 border-none"
              name="item"
            >
              <template v-slot:card-header>
                <p class="text-xl font-bold leading-normal text-white">{{ item.title }}</p>
              </template>
              <template v-slot:card-description>
                <p class="text-base font-normal leading-normal text-gray-350">
                  {{ item.subtitle }}
                </p>
              </template>
            </CardWrapper>
          </template>
        </ListWrapper>
        <div
          class="max-w-1360 mx-2.5 py-8 px-0 justify-evenly bg-gray-850 rounded-2.5 md:hidden flex flex-col px-2.5"
        >
          <p class="text-2xl font-bold leading-normal text-white">
            {{ advantages[0].title + ' ' + advantages[0].subtitle }}
          </p>
          <ListWrapper
            :itemsList="advantages.slice(1)"
            class="grid grid-cols-2 px-0 pt-6 mx-auto justify-evenly gap-y-6"
          >
            <template v-slot="{ item }">
              <CardWrapper
                :cardDetails="item"
                class="flex flex-col items-center col-span-1 p-0 border-none"
                name="item"
              >
                <template v-slot:card-header>
                  <p class="text-xl font-bold leading-normal text-white">{{ item.title }}</p>
                </template>
                <template v-slot:card-description>
                  <p class="text-base font-normal leading-normal text-gray-350">
                    {{ item.subtitle }}
                  </p>
                </template>
              </CardWrapper>
            </template>
          </ListWrapper>
        </div>
      </template>
    </SectionWrapper>
  </div>
</template>
